import { useGetEligiblePlans } from './hooks';
import { PlansList } from './PlansList';
import { InvalidLoanAmountState } from './InvalidLoanAmountState';
import { NoPlansAvailableState } from './NoPlansAvailableState';
import { Calculation, ErrorInfo } from '@servicetitan/financing-api-client';
import { SetupDataStore, useProviderConfig } from '@servicetitan/financing-workflow-shared';
import { useLoanAmountValidation } from '../providers/useLoanAmountValidation';
import { FinancedAmountStore } from '../financing-information/financedAmountStore';
import { LoadingScreen } from '@servicetitan/financing-utils-shared';
import { CustomPlansStore } from './CustomPlansStore';

declare module '@tanstack/react-query' {
    interface Register {
        defaultError: ErrorInfo;
    }
}

interface ContainerProps {
    onPlanSelected?: (plan: Calculation) => void;
}

export const Container = (props: ContainerProps) => {
    const { financedAmount } = FinancedAmountStore.useStore();
    const { setupData } = SetupDataStore.useStore();
    const {
        customPlanIds,
        hasCustomPlans,
        provider: providerFromUrl,
    } = CustomPlansStore.useStore();
    const { planSelectionInstructions } = useProviderConfig();
    // When the currently selected provider is the same as the provider from the URL, use the custom plans
    const useCustomPlans = providerFromUrl === setupData?.provider && hasCustomPlans;

    const { data, isLoading } = useGetEligiblePlans({
        providerName: setupData?.provider ?? '',
        tenantId: setupData?.tenantId ?? null,
        buId: setupData?.buId ?? null,
        amount: financedAmount!,
        allPlans: true,
        state: setupData?.state,
        useAdditionalPromotions: true,
        planList: useCustomPlans ? customPlanIds : null,
        // For FinanceIt, always request all payment frequencies to get comprehensive plan data
        paymentFrequency: setupData?.provider?.toLowerCase() === 'financeit' ? 'all' : null,
        merchantId: 'all',
    });

    const { noPlansEligible, minimumAmount } = data ?? {};
    const validation = useLoanAmountValidation(financedAmount);

    return isLoading ? (
        <LoadingScreen message="Loading available plans..." />
    ) : !validation.isValidLoanAmount ? (
        <InvalidLoanAmountState
            minimumLoanAmount={validation.minAmount}
            maximumLoanAmount={validation.maxAmount}
        />
    ) : data?.data.length === 0 && noPlansEligible ? (
        <InvalidLoanAmountState minimumLoanAmount={minimumAmount!} />
    ) : data?.data.length === 0 ? (
        <NoPlansAvailableState />
    ) : data != null ? (
        <PlansList plans={data?.data} instructions={planSelectionInstructions} {...props} />
    ) : null;
};
